import UIActions from "../actions/UIActions";
import BlockUtils from "./BlockUtils";
import AppConstants from "../constants/AppConstants";
import AssetActions from "../actions/AssetActions";
import FileUtils from "./FileUtils";
import AssetUtils from "./AssetUtils";
import AppConfig from "@/config/app";

const {
  ITEM_TYPE_BLOCK,
  BLOCK_PROP_PARENT_BLOCK,
  PANEL_SECTION_ELEMENT_GRAPHIC,
  PANEL_SECTION_ELEMENT_LOTTIE,
  ASSET_TYPE_IMAGE_EXTS,
  ASSET_TYPE_LOTTIE_EXTS,
} = AppConstants;

const DRAG_DROP_SUPPORTED_EXTS = [
  ...ASSET_TYPE_IMAGE_EXTS,
  ...ASSET_TYPE_LOTTIE_EXTS,
  AppConstants.EXT_JSON,
];

const ELEMENT_CREATION_BUFFER_MS = 500;
const TOAST_DURATION = 5000;

/**
 * Filters array to only include valid image files.
 *
 * @param {File[]} [files=[]] - Array of files to filter
 * @returns {File[]} Array containing only valid image files
 */
export const getImageFiles = (files = []) => {
  return Array.isArray(files) ? files.filter(FileUtils.isImageFile) : [];
};

/**
 * Filters array to include valid image or Lottie files (supported for drag-and-drop).
 *
 * @param {File[]} [files=[]] - Array of files to filter
 * @returns {File[]} Array containing valid image or Lottie files
 */
export const getSupportedMediaFiles = (files = []) => {
  return Array.isArray(files)
    ? files.filter((file) =>
        FileUtils.isValidFileType(file, DRAG_DROP_SUPPORTED_EXTS)
      )
    : [];
};

/**
 * Gets the first valid image file from an array.
 *
 * @param {File[]} [files=[]] - Array of files to search
 * @returns {File|null} First valid image file or null if none found
 */
export const getFirstImageFile = (files = []) => {
  const imageFiles = getImageFiles(files);
  return imageFiles[0] || null;
};

/**
 * Analyzes files to identify unsupported types.
 *
 * @param {File[]} [files=[]] - Array of files to analyze
 * @returns {Object} Analysis result with hasUnsupportedFiles and firstUnsupportedExtension properties
 */
export const getUnsupportedFileInfo = (files = []) => {
  const emptyResult = {
    hasUnsupportedFiles: false,
    firstUnsupportedExtension: null,
  };

  if (!Array.isArray(files) || files.length === 0) {
    return emptyResult;
  }

  const unsupportedFile = files.find(
    (file) => !FileUtils.isValidFileType(file, DRAG_DROP_SUPPORTED_EXTS)
  );
  if (!unsupportedFile) {
    return emptyResult;
  }

  const extension = FileUtils.getMediaExtension(unsupportedFile.name);
  return {
    hasUnsupportedFiles: true,
    firstUnsupportedExtension: extension ?? "unknown",
  };
};

/**
 * Performs comprehensive analysis of a file batch.
 *
 * @param {File[]} [files=[]] - Array of files to analyze
 * @returns {Object} Detailed analysis results with status, counts, and file type information
 */
export const analyzeFileBatch = (files = []) => {
  const baseResult = {
    status: "empty",
    hasUnsupportedFiles: false,
    hasSupportedFiles: false,
    firstUnsupportedExtension: null,
    supportedCount: 0,
    unsupportedCount: 0,
    totalCount: 0,
  };

  if (!Array.isArray(files) || files.length === 0) {
    return baseResult;
  }

  const supportedFiles = getSupportedMediaFiles(files);
  const unsupportedInfo = getUnsupportedFileInfo(files);

  const supportedCount = supportedFiles.length;
  const unsupportedCount = files.length - supportedCount;

  let status;
  if (unsupportedCount === 0) {
    status = "all_supported";
  } else if (supportedCount === 0) {
    status = "all_unsupported";
  } else {
    status = "mixed";
  }

  return {
    status,
    hasUnsupportedFiles: unsupportedInfo.hasUnsupportedFiles,
    hasSupportedFiles: supportedCount > 0,
    firstUnsupportedExtension: unsupportedInfo.firstUnsupportedExtension,
    supportedCount,
    unsupportedCount,
    totalCount: files.length,
  };
};

/**
 * Selects a block/canvas for image drop operations.
 *
 * @param {Object} params - Selection parameters
 * @param {Object} params.block - The block data (Immutable.js object)
 * @param {string} params.layout - The current layout identifier
 */
export const selectBlockForImageDrop = ({ block, layout }) => {
  if (!block || !layout) return;

  UIActions.itemOut();
  UIActions.selectBlock({
    id: block.get("id"),
    layout,
  });
};

/**
 * Enters site block edit mode for image drop operations.
 *
 * @param {Object} params - Parameters for entering site block edit mode
 * @param {Object} params.block - The block data (Immutable.js object)
 * @param {string} params.layout - The current layout identifier
 */
export const enterSiteBlockEditModeForImageDrop = ({ block, layout }) => {
  if (!block || !layout) return;

  const baseBlock = block.get(BLOCK_PROP_PARENT_BLOCK);
  if (!baseBlock) return;

  UIActions.itemOut();
  UIActions.selectSiteBlock({ block: baseBlock, layout });
  UIActions.addActiveItemHistory({ type: ITEM_TYPE_BLOCK });
};

/**
 * Determines if a block should be selected for image drop based on its current state.
 *
 * @param {Object} params - Parameters for determining selection
 * @param {Object} params.block - The block data (currently unused but kept for API consistency)
 * @param {boolean} params.active - Whether the block is currently active/selected
 * @returns {boolean} True if the block should be selected
 */
export const shouldSelectBlockForImageDrop = ({ block, active }) => {
  return !active;
};

/**
 * Handles dropping an image or Lottie file onto a block/canvas.
 * Orchestrates block selection and asset preparation.
 *
 * @param {Object} params - Parameters for handling the media drop
 * @param {File[]} [params.files=[]] - Dropped files
 * @param {Object} params.block - The block data (Immutable.js object)
 * @param {string} params.layout - The current layout identifier
 * @param {boolean} params.active - Whether the block is currently active/selected
 * @returns {boolean} True if drop handling was initiated successfully
 */
export const handleImageDrop = ({ files = [], block, layout, active }) => {
  const mediaFiles = getSupportedMediaFiles(files);
  if (mediaFiles.length === 0) return false;

  const usingSiteBlock = BlockUtils.isUsingSiteBlock(block);

  if (usingSiteBlock) {
    enterSiteBlockEditModeForImageDrop({ block, layout });
  } else if (shouldSelectBlockForImageDrop({ block, active })) {
    selectBlockForImageDrop({ block, layout });
  }

  AssetActions.uploadFiles({
    files: [mediaFiles[0]],
    folder_id: null,
  });

  return true;
};

/**
 * Creates a promise-based upload completion waiter.
 * Listens for asset upload completion events and resolves when matching asset is found.
 *
 * @param {File} file - The file being uploaded
 * @returns {Object} Object with promise and cancel function
 */
export const waitForImageUploadCompletion = (file) => {
  if (!file || typeof file !== "object") {
    return { promise: Promise.resolve(null) };
  }

  const fileName = file.name || "";
  const fileSize = file.size || 0;
  const matchKey = `${fileName}::${fileSize}`;

  const promise = new Promise((resolve, reject) => {
    let unlisten = null;
    try {
      unlisten = AssetActions.completeAssetUpload.listen((asset) => {
        if (asset && `${asset.name || ""}::${asset.size || 0}` === matchKey) {
          if (unlisten) unlisten();
          resolve(asset);
        }
      });
    } catch (error) {
      if (unlisten) unlisten();
      reject(error);
    }
  });

  return { promise };
};

/**
 * Creates an element from an uploaded image or Lottie asset.
 * Includes buffer time for smooth UX and proper error handling.
 *
 * @param {Object} asset - The completed uploaded asset with ext, key, etc.
 * @returns {Promise<void>} Promise that resolves after element creation and buffer time
 * @throws {Error} Re-throws any errors for caller to handle
 */
export const createElementFromImageAsset = async (asset) => {
  if (!asset?.ext) return;

  if (!asset.key) return;

  try {
    AssetActions.createElements([asset]);

    if (AssetUtils.isLottieAsset(asset)) {
      UIActions.toggleElementPropertiesSection({
        name: PANEL_SECTION_ELEMENT_LOTTIE,
      });
    } else if (AppConstants.ASSET_TYPE_IMAGE_EXTS.includes(asset.ext)) {
      UIActions.toggleElementPropertiesSection({
        name: PANEL_SECTION_ELEMENT_GRAPHIC,
      });
    }

    await new Promise((resolve) =>
      setTimeout(resolve, ELEMENT_CREATION_BUFFER_MS)
    );
  } catch (error) {
    throw error;
  }
};

/**
 * Generic function to show drag and drop toast messages.
 * @param {string} message - The toast message to show
 * @param {"warning"|"error"|"info"} [status="warning"] - The toast status level
 * @param {number} [dur=TOAST_DURATION] - Duration of the toast in milliseconds
 */
const showDragDropToast = (
  message,
  status = "warning",
  dur = TOAST_DURATION,
  hasClose = false
) => {
  if (!message?.trim()) return;

  UIActions.showToast({
    message: message.trim(),
    status,
    hasClose,
    dur,
  });
};

/**
 * Validates dropped files and shows appropriate warning messages.
 *
 * @param {File[]} files - Array of dropped files
 * @returns {Object} Validation result with hasUnsupportedFiles, totalCount, and hasSupportedFiles
 */
export const validateAndShowDropWarnings = (files) => {
  const batchAnalysis = analyzeFileBatch(files);

  // Show individual toast messages for drag and drop scenarios
  if (batchAnalysis.hasUnsupportedFiles) {
    const message = batchAnalysis.firstUnsupportedExtension
      ? `.${batchAnalysis.firstUnsupportedExtension} is not supported by drag-and-drop`
      : "This file type is not supported";
    showDragDropToast(message, "warning");
  }

  if (batchAnalysis.totalCount > 1) {
    showDragDropToast("Please drop one file at a time", "warning");
  }

  return batchAnalysis;
};

/**
 * Executes the complete image drop flow including upload overlay, waiting for completion, and element creation.
 *
 * @param {Object} params - Parameters for the drop flow
 * @param {File[]} params.files - Array of dropped files
 * @param {Object} params.block - The block data
 * @param {string} params.layout - The current layout identifier
 * @param {boolean} params.active - Whether the block is currently active
 * @returns {Promise<void>} Promise that resolves when the flow is complete
 */
export const executeImageDropFlow = async ({
  files,
  block,
  layout,
  active,
}) => {
  const started = handleImageDrop({ files, block, layout, active });
  if (!started) {
    return;
  }

  UIActions.showImageUploadOverlay();
  const firstFile = getSupportedMediaFiles(files)[0] || null;
  const { promise } = waitForImageUploadCompletion(firstFile);

  try {
    const completedAsset = await promise;
    if (!completedAsset) {
      return;
    }

    if (
      completedAsset.ext === AppConstants.EXT_JSON &&
      !AssetUtils.isLottieAsset(completedAsset)
    ) {
      showDragDropToast(
        "This JSON file is not a Lottie animation",
        "warning",
        0,
        true
      );
      return;
    }

    await createElementFromImageAsset(completedAsset);
  } catch (error) {
    showDragDropToast("Something went wrong", "error");
  } finally {
    UIActions.hideImageUploadOverlay();
  }
};

/**
 * Main orchestrator function that processes an image drop from start to finish.
 *
 * @param {Object} params - Parameters for processing the drop
 * @param {File[]} params.files - Array of dropped files
 * @param {Object} params.block - The block data
 * @param {string} params.layout - The current layout identifier
 * @param {boolean} params.active - Whether the block is currently active
 */
export const processImageDrop = ({ files, block, layout, active }) => {
  // validate before we do anything
  if (
    AppConfig.demoMode ||
    !Array.isArray(files) ||
    files.length === 0 ||
    !block ||
    !layout
  ) {
    return;
  }

  const batchAnalysis = validateAndShowDropWarnings(files);
  if (!batchAnalysis.hasSupportedFiles) {
    return;
  }

  executeImageDropFlow({ files, block, layout, active });
};
